import React from "react";

import Badge from "../../../../components/ui/Badge";

/**
 * A quote's status, as ONE badge.
 *
 * Was a coloured box carrying a white SVG glyph — a tick for approved, a spinner for pending,
 * nothing at all for expired. Three different marks for one field, none of them readable without
 * hovering for the tooltip, and an empty box for a third of the rows (Guillaume, 2026-09-01:
 * "multiple icons on the status of every quote"). It now reads as a word, tone-coded through the
 * app's one status map, exactly like every other status in the product.
 *
 * The old `<img>`-not-background trick is no longer needed: `Badge` paints with tokens on its own
 * element, so the legacy `tbody td div { background: transparent !important }` resets that forced
 * that workaround have nothing to strip.
 */
const STATUS_LABELS = {
  approved: "Active",
  accepted_by_client: "Active",
  quote_sent: "Active",
  created: "Draft",
  pending: "Pending",
  to_review: "Pending",
  expired: "Expired",
  rejected: "Expired",
  cancelled: "Expired",
};

// Quote vocabulary → the shared tone names. `statusMap` knows "pending" and "expired" already;
// these are the ones it cannot guess.
const STATUS_TONES = {
  approved: "success",
  accepted_by_client: "success",
  quote_sent: "accent",
  created: "muted",
  pending: "warning",
  to_review: "warning",
  expired: "muted",
  rejected: "danger",
  cancelled: "muted",
};

const sameRequestId = (left, right) => {
  if (left == null || right == null) return false;
  return String(left) === String(right);
};

const StatusIcon = ({ item, requestId }) => {
  const status = typeof item.status === "string" ? item.status.toLowerCase() : item.status;
  const quoteRequestId = item.request_id ?? requestId;

  const isApprovedStatus =
    status === "approved" ||
    status === "accepted_by_client" ||
    status === "quote_sent";

  // "Active" is reserved for the quote that is live for THIS request; an approved quote belonging
  // to another request reads as "Approved" so the two are not confused at a glance.
  const approvedAndBelongs =
    isApprovedStatus && sameRequestId(requestId, quoteRequestId);

  const label = approvedAndBelongs
    ? "Active"
    : isApprovedStatus
      ? "Approved"
      : STATUS_LABELS[status] || item.status?.replace(/_/g, " ") || "Pending";

  // Ownership decides the tone as well as the label. An approved quote belonging to ANOTHER
  // request is not this deal's live quote, so it must not wear the success tone — reading green
  // here would claim a deal is settled on the strength of someone else's quote.
  const tone = isApprovedStatus
    ? (approvedAndBelongs ? "success" : "muted")
    : STATUS_TONES[status] || "muted";

  return (
    <Badge tone={tone} size="sm" dot>
      {label}
    </Badge>
  );
};

export default StatusIcon;
